<template>
  <loading v-if="!isInit"></loading>
  <div class="yuukyuu" v-else>
    <h3>有給休暇申請</h3>
    <div class="message-area" v-if="getError(form, 'yuukyuu')">
      <div class="message danger">
        <p>{{ getError(form, 'yuukyuu') }}</p>
        <p>取得日時点の有給残日数で判定しています。後日付与される有給は、付与日前の取得には使用できません。</p>
        <p>取得日・取得区分を見直すか、有給の付与状況を確認してください。</p>
      </div>
    </div>
    <div class="delete-table">
      <YuukyuuAll :form="form" :koyouKeiyakuId="koyouKeiyakuId" :workTimeId="workTimeId"
        @changeKoyouKeiyakuTime="changeKoyouKeiyakuTime"></YuukyuuAll>
      <!--
        <p class="btn-table-add">
          <button type="button" class="btn primary" @click="addRow">
            <i class="fa-solid fa-plus"></i>追加
          </button>
        </p>
      -->
    </div>
    <div>
      <b class="required">事由</b>
      <input-textarea v-model="form.shinsei_jiyuu" :error="getError(form, 'shinsei_jiyuu')"></input-textarea>
    </div>
    <div>
      <b>備考</b>
      <input-textarea v-model="form.notes" :error="getError(form, 'notes')"></input-textarea>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, toRefs, onMounted, ref, Ref, nextTick } from "vue";
import { useYuukyuu } from "@/composable/shinsei/yuukyuu/yuukyuu";
import { useForm } from "@/composable/form/form";
import { useTableRow } from "@/composable/common/tablerow";
import { Shinsei, ShinseiKoyouKeiyakuTime, Yuukyuu } from '@/types/Shinsei';
import OptionConst from '@/const/Option';
import YuukyuuAll from "./yuukyuu_parts/YuukyuuAll.vue";
import { useAuthStore } from '@/store/auth';
import { DialogService } from "@/service/DialogService";
import { Logger } from "@/utils/Logger";
import { SyuugyouBasyoAndKoyouKeiyakuOption } from "@/types/SyuugyouBasyo";
import { KoyouKeiyakuTimeAndHayabanOsobanOption, KoyouKeiyakuTimeAndHayabanOsobanOptions } from '@/types/KoyouKeiyakuTime';

export default defineComponent({
  props: {
    form: {
      type: Object as () => Shinsei,
      default: null
    },
    selectSyuugyouBasyo: {
      type: Object as () => SyuugyouBasyoAndKoyouKeiyakuOption | null,
      default: null
    }
  },
  emits: {
    changeKoyouKeiyakuTime: (selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null, selectedKoyouKeiyakuTimeOption: ShinseiKoyouKeiyakuTime | null) => true,
  },
  setup(props, context) {
    const { addRowNoRef, removeTableRow } = useTableRow();
    const { form } = toRefs(props);
    const { getZanYuukyuu, zanYuukyuu, isLoading } = useYuukyuu();
    const authStore = useAuthStore();

    // エラー取得
    const { getError } = useForm();

    // 編集モードで使用する就業場所（雇用契約）と就業時間の初期値
    // 初期化時のみ使用し、以降は使用せずnullにする（実データはリクエストで送信される申請オブジェクト内に保持されるため）
    let workTimeId: Ref<number | null> = ref(null)
    let koyouKeiyakuId: Ref<number | null> = ref(null)

    const isInit = ref(false)

    onMounted(async () => {
      Logger.debug('----------------------------\n有休申請コンポーネントの初期化開始')
      Logger.debug('有休申請 onMounted', { '申請': form.value })

      nextTick(async () => {
        const userId = authStore.user?.user_id
        if (userId) {
          // 有休残数の取得（就業場所問わず）
          zanYuukyuu.value = await getZanYuukyuu(userId)
        }
        if (!form.value.yuukyuu) {
          Logger.debug('新規作成モードの初期化を行います')
          form.value.yuukyuu = [{
            start_date: null,
            yuukyuu_tani_type: null,
            time: null,
            yuukyuu_times: null,
          }]
        } else {
          Logger.debug('編集モードの初期化を行います')
          workTimeId.value = form.value.shinsei_koyou_keiyaku_time.length > 0 ? form.value.shinsei_koyou_keiyaku_time[0].koyou_keiyaku_time_id : null
          koyouKeiyakuId.value = form.value.koyou_keiyaku_id ?? null
          Logger.debug('時間外申請より就業場所（雇用契約）と就業時間を初期化しました', { '雇用契約ID': koyouKeiyakuId.value, '就業時間ID': workTimeId.value })
        }
        isInit.value = true
        Logger.debug('時間外申請コンポーネントの初期化完了\n----------------------------')
      })
    })

    // 行追加
    const addRow = () => {
      const row: Yuukyuu = {
        start_date: null,
        time: null,
        yuukyuu_tani_type: null,
        yuukyuu_times: null
      }
      addRowNoRef(form.value.yuukyuu, row)
    }

    const alertZanYuukyuu = async () => {
      if (!form.value.yuukyuu) return
      let res = true
      const syutokuDays = form.value.yuukyuu.reduce(function (sum, v) {
        const days = v.yuukyuu_tani_type == OptionConst.SHINSEI_DIV.FULL_DAY ? 1 : 0.5
        return sum + days
      }, 0)
      const zan = zanYuukyuu.value?.zan_yuukyuu - syutokuDays

      if (zan < 0) {
        await DialogService.warning({
          title: "有給が足りません",
          text: "有給残り日数：" + zanYuukyuu.value.zan_yuukyuu + "日",
        })
        return false
      }

      if (zan <= 5) {
        await DialogService.warning({
          title: "有給が5日以下になります",
          text: "申請が承認された場合に、有給が残り" + zan + "日になります。",
          confirmButtonText: '申請する',
          showCancelButton: true,
          cancelButtonText: 'キャンセル',
        }).then((result) => {
          if (!result.isConfirmed) {
            res = false
          }
        })
      }
      return res
    }

    // 就業場所・就業時間が変更された場合に走る処理
    const changeKoyouKeiyakuTime = (
      targetDate: string | Date | null,
      selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null,
      selectedKoyouKeiyakuTimeOption: KoyouKeiyakuTimeAndHayabanOsobanOption | null,
      koyouKeiyakuTimeAndHayabanOsobanOptions: KoyouKeiyakuTimeAndHayabanOsobanOptions
    ) => {
      Logger.debug('有給申請 子コンポーネントの変更を受信')
      // 就業時間に関する情報のセット
      let koyouKeiyakuTime: ShinseiKoyouKeiyakuTime | null = null
      if (selectedKoyouKeiyakuTimeOption) {
        // 申請しようとしている有給の個数分のコンポーネントをループ
        // 通常勤務時間
        let tsuujouTime: string | null = "00:00:00";

        // 半日有給取得可能かどうかのフラグ
        let isHannichi = false;
        // AM有給しか取得できない、指定した通常時間が有給取得時間
        let canOnlyAmKinmu = 0;
        // PM有給しか取得できない、指定した通常時間が有給取得時間
        let canOnlyPmKinmu = 0;

        if (koyouKeiyakuTimeAndHayabanOsobanOptions.hayabanOsobanOpt.length > 0) {
          isHannichi = true
          Logger.debug('早番・遅番申請が存在するため半日フラグをセットしました', koyouKeiyakuTimeAndHayabanOsobanOptions.hayabanOsobanOpt)
        }

        // 通常勤務時間を取得できる場合
        if (selectedKoyouKeiyakuTimeOption && selectedKoyouKeiyakuTimeOption.tsuujouTime) {
          tsuujouTime = selectedKoyouKeiyakuTimeOption.tsuujouTime
          Logger.debug('選択された就業時間から通常時間をセットしました', tsuujouTime)
        }

        // 半日有給取得可能かどうかのフラグを取得できる場合
        if (selectedKoyouKeiyakuTimeOption && selectedKoyouKeiyakuTimeOption.isHannichi) {
          isHannichi = selectedKoyouKeiyakuTimeOption.isHannichi
          Logger.debug('選択された就業時間から半日フラグをセットしました', isHannichi)
        }

        // AM有給しか取得できないかどうかのフラグを取得できる場合
        if (selectedKoyouKeiyakuTimeOption && selectedKoyouKeiyakuTimeOption.canOnlyAmKinmu) {
          canOnlyAmKinmu = selectedKoyouKeiyakuTimeOption.canOnlyAmKinmu
          tsuujouTime = selectedKoyouKeiyakuTimeOption.tsuujouTime
          Logger.debug('選択された就業時間から「AM勤務のみ取得可フラグ」と通常時間をセットしました', { "AM勤務のみ取得可フラグ": canOnlyAmKinmu, "通常時間": tsuujouTime })
        }

        // PM有給しか取得できないかどうかのフラグを取得できる場合
        if (selectedKoyouKeiyakuTimeOption && selectedKoyouKeiyakuTimeOption.canOnlyPmKinmu) {
          canOnlyPmKinmu = selectedKoyouKeiyakuTimeOption.canOnlyPmKinmu
          tsuujouTime = selectedKoyouKeiyakuTimeOption.tsuujouTime
          Logger.debug('選択された就業時間から「PM勤務のみ取得可フラグ」と通常時間をセットしました', { "PM勤務のみ取得可フラグ": canOnlyPmKinmu, "通常時間": tsuujouTime })
        }

        // 有休申請で使用する雇用契約時間にデータを保存する
        koyouKeiyakuTime = {
          shinsei_id: form.value.shinsei_id,
          koyou_keiyaku_time_id: selectedKoyouKeiyakuTimeOption.val,
          target_date: targetDate,
          tsuujouTime: tsuujouTime,
          isHannichi: isHannichi,
          canOnlyAmKinmu: canOnlyAmKinmu,
          canOnlyPmKinmu: canOnlyPmKinmu,
        }
        Logger.debug('申請雇用契約時間をセットしました', form.value.shinsei_koyou_keiyaku_time)
      }

      koyouKeiyakuId.value = selectedSyuugyouBasyo?.koyou_id ?? null
      workTimeId.value = selectedKoyouKeiyakuTimeOption?.val ?? null

      // 就業場所に関する情報をemitする（申請オブジェクトの変更等は親に移譲する）
      // なお、現状は１画面で１件（＝１就業場所）しか登録できないため、申請オブジェクトに雇用契約ID等を保存している
      // 公休のように１画面で複数件か１公休ごとに異なる就業場所を登録させるためにはこの構造自体を変える必要がある
      // 申請オブジェクトに雇用契約IDをもたせるのではなく、yuukyuu配列の各要素に持たせる必要がある
      // ただし公休に倣えばいいかとそうでもなく、公休は申請とは別の仕組みのため新規で処理を起こす必要がある
      context.emit('changeKoyouKeiyakuTime', selectedSyuugyouBasyo, koyouKeiyakuTime);
    }

    return {
      form,
      getError,
      addRow,
      addRowNoRef,
      removeTableRow,
      'kyuukaDiv': OptionConst.SHINSEI_DIV_OPTION,
      zanYuukyuu,
      alertZanYuukyuu,
      isLoading,
      isInit,
      workTimeId,
      koyouKeiyakuId,
      changeKoyouKeiyakuTime
    };
  },
  components: {
    YuukyuuAll,
  },
});
</script>

<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
</style>
